// ============================================================================
// User Profile Checker - Ensures user has a Firestore profile document
// Automatically creates missing user profiles to prevent permission errors
// ============================================================================

import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/services/firebase';

export function UserProfileChecker() {
  const { currentUser } = useAuth();
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fixed, setFixed] = useState(false);

  useEffect(() => {
    if (currentUser && !checking) {
      checkAndFixUserProfile();
    }
  }, [currentUser]);

  const checkAndFixUserProfile = async () => {
    if (!currentUser) return;

    setChecking(true);
    setError(null);

    try {
      // Check if user profile exists
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        console.warn('User profile missing in Firestore. Creating...');

        // Create user profile with default values
        await setDoc(userDocRef, {
          displayName: currentUser.displayName || currentUser.email?.split('@')[0] || 'User',
          email: currentUser.email,
          role: 'Ward_Nurse', // Default role
          facilityId: '', // Will need to be assigned by admin
          isActive: true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          lastLoginAt: serverTimestamp()
        });

        console.log('✅ User profile created successfully');
        setFixed(true);

        // Reload the page to refresh auth context
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    } catch (err: any) {
      console.error('Error checking/fixing user profile:', err);
      setError(err.message);
    } finally {
      setChecking(false);
    }
  };

  // Show notification if profile was just created
  if (fixed) {
    return (
      <div className="fixed top-4 right-4 z-50 max-w-md">
        <div className="bg-success-50 border border-success-200 text-success-700 px-4 py-3 rounded-lg shadow-lg">
          <p className="font-semibold">Profile Created</p>
          <p className="text-sm mt-1">
            Your user profile has been created. Refreshing the page...
          </p>
        </div>
      </div>
    );
  }

  // Show error if profile creation failed
  if (error) {
    return (
      <div className="fixed top-4 right-4 z-50 max-w-md">
        <div className="bg-danger-50 border border-danger-200 text-danger-700 px-4 py-3 rounded-lg shadow-lg">
          <p className="font-semibold">Profile Error</p>
          <p className="text-sm mt-1">{error}</p>
          <button
            onClick={checkAndFixUserProfile}
            className="mt-2 text-xs underline"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return null;
}
